/**
 * Phase 13 — Admin PWS Reporting page.
 *
 * Read-only reporting dashboard for the Aware Partner Network at
 * /admin/partners/reporting. Surfaces 8 reports in sequential cards:
 *
 *   0. Partner | Link | Region | Total | Revenue | Commission (Feb 2026)
 *   1. Partner Booking Summary       (per-partner roll-up)
 *   2. Source Attribution Report     (counts by booking_source)
 *   3. Pricing / Revenue Snapshot    (sums by pricing_mode)
 *   4. Kit Workflow Report           (kit status counts + exception chips)
 *   5. Commission Report             (ledger by status)
 *   6. White-Label Report Summary    (branding mode + fallback reasons)
 *   7. Attention / Exception Report  (items needing admin attention)
 *
 * All data is fetched in parallel on mount + window-filter change.
 * Date window selector (7d / 30d / month / all) applies globally.
 * The combined summary card additionally supports its own explicit
 * start/end date range, which overrides the global window for that
 * card only. Three CSV download buttons (combined summary, booking
 * summary, source attribution).
 *
 * SAFETY: this page makes ONLY `GET` requests. There are NO mutation
 * controls (no buttons that POST/PATCH/DELETE). Aligns with Phase 13's
 * read-only mandate.
 */
import { useEffect, useMemo, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, AlertTriangle, Download, BarChart3,
  Users, Target, DollarSign, Package, Wallet, FileText, Bell,
  Loader2, Eye,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";

const WINDOWS = [
  { id: "all",        label: "All time" },
  { id: "this_month", label: "This month" },
  { id: "last_30d",   label: "Last 30d" },
  { id: "last_7d",    label: "Last 7d" },
];

const ATTENTION_LABELS = {
  review_required:              "Region match · REVIEW REQUIRED",
  partner_inactive_or_missing:  "Partner inactive / missing",
  kit_pending_over_48h:         "Kit pending > 48h",
  report_fallback:              "Summary fell back to SCWT-branded",
  commission_blocked:           "Commission blocked (no ledger entry)",
  notification_suppressed:      "Notification suppressed / disabled",
  missing_kit_tracking:         "Missing tracking after kit sent",
};

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtNum = (n) => Number(n || 0).toLocaleString();

async function downloadCsv(path, filename) {
  try {
    const { data } = await api.get(path, { responseType: "blob" });
    const blob = new Blob([data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(formatApiError(e) || "CSV download failed");
  }
}

function Card({ icon: Icon, title, testId, children, headerExtra }) {
  return (
    <section
      data-testid={testId}
      className="rounded-2xl bg-white border border-slate-200 p-5"
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-cyan-700 shrink-0" />
          <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700">
            {title}
          </h3>
        </div>
        {headerExtra}
      </div>
      {children}
    </section>
  );
}

function StatChip({ label, value, tone = "bg-slate-50", testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-lg ${tone} p-3 border border-slate-100`}
    >
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-bold text-slate-800">{value}</div>
    </div>
  );
}

function WindowSelector({ window, onChange }) {
  return (
    <div
      className="inline-flex rounded-full bg-slate-100 p-1 text-xs font-semibold"
      data-testid="ph13-window-selector"
    >
      {WINDOWS.map(w => (
        <button
          key={w.id}
          onClick={() => onChange(w.id)}
          data-testid={`ph13-window-${w.id}`}
          className={`px-3 py-1 rounded-full transition ${
            window === w.id
              ? "bg-cyan-600 text-white shadow-sm"
              : "text-slate-600 hover:text-slate-800"
          }`}
        >
          {w.label}
        </button>
      ))}
    </div>
  );
}


// ── 1. Partner Booking Summary ──────────────────────────────────────────
function PartnerBookingSummary({ data, window }) {
  const rows = data?.rows || [];
  return (
    <Card
      icon={Users}
      title={`Partner Booking Summary (${rows.length})`}
      testId="ph13-booking-summary"
      headerExtra={
        <button
          onClick={() => downloadCsv(
            `/admin/pws-reporting/booking-summary.csv?window=${window}`,
            `pws_booking_summary_${window}.csv`,
          )}
          data-testid="ph13-booking-summary-csv"
          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700"
        >
          <Download className="w-3 h-3" /> CSV
        </button>
      }
    >
      {rows.length === 0 ? (
        <div data-testid="ph13-booking-summary-empty" className="text-xs text-slate-400 italic py-2">
          No partner-attributed bookings in this window.
        </div>
      ) : (
        <div className="overflow-auto -mx-2">
          <table className="w-full text-xs" data-testid="ph13-booking-summary-table">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left p-2">Partner</th>
                <th className="text-right p-2">Total</th>
                <th className="text-right p-2">Link</th>
                <th className="text-right p-2">Stored</th>
                <th className="text-right p-2">Region</th>
                <th className="text-right p-2">Review</th>
                <th className="text-right p-2">Paid</th>
                <th className="text-right p-2">Published</th>
                <th className="text-right p-2">Pending</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr
                  key={r.partner_id}
                  data-testid={`ph13-booking-summary-row-${r.partner_id}`}
                  className="border-t border-slate-100"
                >
                  <td className="p-2">
                    <div className="font-semibold text-slate-800">
                      {r.business_name || <span className="text-slate-400 italic">(unknown)</span>}
                    </div>
                    <div className="font-mono text-[10px] text-slate-500">{r.partner_slug || r.partner_id}</div>
                  </td>
                  <td className="p-2 text-right font-bold">{fmtNum(r.total)}</td>
                  <td className="p-2 text-right">{fmtNum(r.partner_link)}</td>
                  <td className="p-2 text-right">{fmtNum(r.stored_attribution)}</td>
                  <td className="p-2 text-right">{fmtNum(r.region_allocation)}</td>
                  <td className="p-2 text-right">
                    {r.review_required > 0 ? (
                      <span className="text-amber-700 font-semibold">{fmtNum(r.review_required)}</span>
                    ) : fmtNum(r.review_required)}
                  </td>
                  <td className="p-2 text-right">{fmtNum(r.paid)}</td>
                  <td className="p-2 text-right">{fmtNum(r.published)}</td>
                  <td className="p-2 text-right">
                    {r.pending_sample > 0 ? (
                      <span className="text-cyan-700 font-semibold">{fmtNum(r.pending_sample)}</span>
                    ) : fmtNum(r.pending_sample)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}


// ── 1b. Partner Combined Summary (Feb 2026 — Partner|Link|Region|Total|Revenue|Commission) ──
function PartnerCombinedSummary({
  window: pageWindow, refreshKey, startDate, endDate, onStartDateChange, onEndDateChange,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const usingCustomRange = Boolean(startDate && endDate);
  const rangeInvalid = usingCustomRange && startDate > endDate;

  useEffect(() => {
    if (rangeInvalid) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    setError(null);
    const params = usingCustomRange
      ? { start_date: startDate, end_date: endDate }
      : { window: pageWindow };
    api.get("/admin/pws-reporting/partner-combined-summary", { params })
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => { if (alive) setError(formatApiError(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageWindow, startDate, endDate, refreshKey, rangeInvalid]);

  const rows = data?.rows || [];
  const csvParams = new URLSearchParams(
    usingCustomRange ? { start_date: startDate, end_date: endDate } : { window: pageWindow },
  );
  const csvUrl = `/admin/pws-reporting/partner-combined-summary.csv?${csvParams.toString()}`;

  return (
    <Card
      icon={Users}
      title={`Partner | Link | Region | Total | Revenue | Commission (${rows.length})`}
      testId="ph13-combined-summary"
      headerExtra={
        <div className="flex items-start gap-2 flex-wrap max-w-full">
          <div className="flex items-center gap-1 flex-wrap" data-testid="ph13-combined-date-range">
            <input
              type="date"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              data-testid="ph13-combined-start-date"
              className="text-[11px] border border-slate-200 rounded-md px-1.5 py-1 w-[128px]"
            />
            <span className="text-[10px] text-slate-400">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              data-testid="ph13-combined-end-date"
              className="text-[11px] border border-slate-200 rounded-md px-1.5 py-1 w-[128px]"
            />
            {usingCustomRange && (
              <button
                onClick={() => { onStartDateChange(""); onEndDateChange(""); }}
                data-testid="ph13-combined-clear-range"
                className="text-[10px] font-semibold text-cyan-700 hover:underline px-1"
              >
                Clear
              </button>
            )}
          </div>
          <button
            onClick={() => downloadCsv(csvUrl, `pws_partner_combined_summary_${usingCustomRange ? `${startDate}_${endDate}` : pageWindow}.csv`)}
            data-testid="ph13-combined-summary-csv"
            disabled={rangeInvalid}
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download className="w-3 h-3" /> CSV
          </button>
        </div>
      }
    >
      <p className="text-[11px] text-slate-400 italic -mt-1 mb-2">
        {usingCustomRange && !rangeInvalid && (
          <span className="not-italic text-cyan-700 font-semibold mr-1" data-testid="ph13-combined-custom-range-hint">
            Custom range active (overrides the global window filter above) ·
          </span>
        )}
        Link Bookings = Partner Link + Stored (90d) attribution. Region Direct = region-allocation
        (no stronger link attribution). Totals never double count. Commission is read from the
        commission ledger — it is <strong>not</strong> recalculated from revenue, and region-direct /
        stored-attribution bookings are not automatically commission-eligible.
      </p>
      {rangeInvalid ? (
        <div data-testid="ph13-combined-range-invalid" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg py-2 px-3">
          Start date must be on or before end date.
        </div>
      ) : loading ? (
        <div data-testid="ph13-combined-loading" className="text-xs text-slate-400 italic py-4 flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      ) : error ? (
        <div data-testid="ph13-combined-error" className="text-xs text-rose-700 py-2">{error}</div>
      ) : rows.length === 0 ? (
        <div data-testid="ph13-combined-empty" className="text-xs text-slate-400 italic py-2">
          No partner-attributed bookings in this window.
        </div>
      ) : (
        <div className="overflow-auto -mx-2">
          <table className="w-full text-xs" data-testid="ph13-combined-summary-table">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left p-2">Partner</th>
                <th className="text-right p-2">Link Bookings</th>
                <th className="text-right p-2">Region Direct</th>
                <th className="text-right p-2">Total Territory</th>
                <th className="text-right p-2">Revenue</th>
                <th className="text-right p-2">Commission</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const isOpen = expanded === r.partner_id;
                const hasReview = (r.region_direct_confidence?.region_match_review_required || 0) > 0;
                return (
                  <Fragment key={r.partner_id}>
                    <tr
                      data-testid={`ph13-combined-row-${r.partner_id}`}
                      className="border-t border-slate-100"
                    >
                      <td className="p-2">
                        <div className="font-semibold text-slate-800">
                          {r.business_name || <span className="text-slate-400 italic">(unknown)</span>}
                        </div>
                        <div className="font-mono text-[10px] text-slate-500">{r.partner_slug || r.partner_id}</div>
                      </td>
                      <td className="p-2 text-right font-bold">{fmtNum(r.link_bookings)}</td>
                      <td className="p-2 text-right">
                        {fmtNum(r.region_direct)}
                        {hasReview && (
                          <span
                            className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded-full bg-amber-100 text-amber-900"
                            title="Includes bookings pending manual region-match review"
                          >
                            {r.region_direct_confidence.region_match_review_required} review
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-right font-bold">{fmtNum(r.total_territory_bookings)}</td>
                      <td className="p-2 text-right">{fmtMoney(r.revenue)}</td>
                      <td className="p-2 text-right">{fmtMoney(r.commission)}</td>
                      <td className="p-2 text-right">
                        <button
                          onClick={() => setExpanded(isOpen ? null : r.partner_id)}
                          data-testid={`ph13-combined-expand-${r.partner_id}`}
                          className="text-[10px] font-semibold text-cyan-700 hover:underline"
                        >
                          {isOpen ? "Hide" : "Breakdown"}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-50/70 border-t border-slate-100" data-testid={`ph13-combined-breakdown-${r.partner_id}`}>
                        <td colSpan={7} className="p-3">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-[11px]">
                            <div>
                              <div className="font-bold text-slate-600 uppercase tracking-wide text-[9px] mb-1">Link bookings</div>
                              <div>Partner link: <strong>{fmtNum(r.link_bookings_breakdown?.partner_link)}</strong></div>
                              <div>Stored (90d): <strong>{fmtNum(r.link_bookings_breakdown?.stored_partner_attribution)}</strong></div>
                            </div>
                            <div>
                              <div className="font-bold text-slate-600 uppercase tracking-wide text-[9px] mb-1">Region direct confidence</div>
                              <div>Confident match: <strong>{fmtNum(r.region_direct_confidence?.region_match)}</strong></div>
                              <div className={hasReview ? "text-amber-700" : ""}>
                                Review required: <strong>{fmtNum(r.region_direct_confidence?.region_match_review_required)}</strong>
                              </div>
                              <div>Nearest-partner fallback: <strong>{fmtNum(r.region_direct_confidence?.nearest_partner_fallback)}</strong></div>
                            </div>
                            <div>
                              <div className="font-bold text-slate-600 uppercase tracking-wide text-[9px] mb-1">Revenue by source</div>
                              <div>Partner link: <strong>{fmtMoney(r.revenue_breakdown?.partner_link)}</strong></div>
                              <div>Stored (90d): <strong>{fmtMoney(r.revenue_breakdown?.stored_partner_attribution)}</strong></div>
                              <div>Region direct: <strong>{fmtMoney(r.revenue_breakdown?.region_allocation)}</strong></div>
                            </div>
                          </div>
                          {Object.keys(r.commission_breakdown || {}).length > 0 && (
                            <div className="mt-2 text-[11px] text-slate-600">
                              <span className="font-bold uppercase tracking-wide text-[9px] mr-1">Commission ledger status:</span>
                              {Object.entries(r.commission_breakdown).map(([k, v]) => (
                                <span key={k} className="mr-3">{k}: <strong>{fmtMoney(v)}</strong></span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}


// ── 2. Source Attribution Report ────────────────────────────────────────
function SourceAttribution({ data, window }) {
  const counts = data?.counts || {};
  const total = data?.total || 0;
  return (
    <Card
      icon={Target}
      title={`Source Attribution (total ${fmtNum(total)})`}
      testId="ph13-source-attribution"
      headerExtra={
        <button
          onClick={() => downloadCsv(
            `/admin/pws-reporting/source-attribution.csv?window=${window}`,
            `pws_source_attribution_${window}.csv`,
          )}
          data-testid="ph13-source-attribution-csv"
          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700"
        >
          <Download className="w-3 h-3" /> CSV
        </button>
      }
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <StatChip testId="ph13-source-partner-link" label="Partner link" value={fmtNum(counts.partner_link)} tone="bg-cyan-50" />
        <StatChip testId="ph13-source-stored" label="Stored attribution" value={fmtNum(counts.stored_attribution)} tone="bg-violet-50" />
        <StatChip testId="ph13-source-region" label="Region allocation" value={fmtNum(counts.region_allocation)} tone="bg-lime-50" />
        <StatChip testId="ph13-source-review" label="Review required" value={fmtNum(counts.region_match_review_required)} tone="bg-amber-50" />
        <StatChip testId="ph13-source-direct" label="Direct Aware" value={fmtNum(counts.direct_scwt)} tone="bg-slate-50" />
        {(counts.other || 0) > 0 && (
          <StatChip testId="ph13-source-other" label="Other" value={fmtNum(counts.other)} />
        )}
      </div>
    </Card>
  );
}


// ── 3. Pricing / Revenue Snapshot ───────────────────────────────────────
function RevenueSnapshot({ data }) {
  const t = data?.totals || {};
  const versions = data?.price_versions || [];
  return (
    <Card icon={DollarSign} title="Pricing / Revenue Snapshot" testId="ph13-revenue">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        <StatChip testId="ph13-revenue-total" label="Total revenue" value={fmtMoney(t.final_price_total)} tone="bg-emerald-50" />
        <StatChip testId="ph13-revenue-national" label="National partner" value={fmtMoney(t.national_partner_revenue)} tone="bg-cyan-50" />
        <StatChip testId="ph13-revenue-local" label="Local Aware" value={fmtMoney(t.local_scwt_revenue)} tone="bg-slate-50" />
        <StatChip testId="ph13-revenue-override" label="Admin override" value={fmtMoney(t.admin_override_revenue)} tone="bg-amber-50" />
        <StatChip testId="ph13-revenue-kit-postage" label="Kit/postage" value={fmtMoney(t.kit_postage_total)} />
        <StatChip testId="ph13-revenue-partner-margin" label="Partner margin" value={fmtMoney(t.partner_margin_total)} tone="bg-violet-50" />
        <StatChip testId="ph13-revenue-avg" label="Avg booking" value={fmtMoney(t.average_booking_value)} />
        <StatChip testId="ph13-revenue-count" label="Paid bookings" value={fmtNum(t.booking_count)} />
      </div>
      {versions.length > 0 && (
        <details className="mt-3" data-testid="ph13-revenue-versions">
          <summary className="text-[10px] uppercase tracking-wider font-bold text-slate-500 cursor-pointer">
            Price version breakdown ({versions.length})
          </summary>
          <ul className="mt-2 space-y-1 text-xs">
            {versions.map(v => (
              <li key={v.price_version || "null"} className="font-mono text-slate-700">
                {v.price_version || "—"} · {fmtNum(v.count)}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}


// ── 4. Kit Workflow Report ──────────────────────────────────────────────
function KitWorkflowReport({ data }) {
  const c = data?.counts || {};
  return (
    <Card icon={Package} title="Kit Workflow" testId="ph13-kit-workflow">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        <StatChip testId="ph13-kit-pending" label="Pending" value={fmtNum(c.pending)} tone="bg-amber-50" />
        <StatChip testId="ph13-kit-packed" label="Packed" value={fmtNum(c.packed)} />
        <StatChip testId="ph13-kit-sent" label="Sent" value={fmtNum(c.sent)} tone="bg-cyan-50" />
        <StatChip testId="ph13-kit-returned" label="Returned" value={fmtNum(c.returned)} tone="bg-emerald-50" />
        <StatChip testId="ph13-kit-not-required" label="Not required" value={fmtNum(c.not_required)} />
        <StatChip
          testId="ph13-kit-pending-48h"
          label="Pending >48h"
          value={fmtNum(data?.pending_over_48h)}
          tone={(data?.pending_over_48h || 0) > 0 ? "bg-rose-50" : "bg-slate-50"}
        />
        <StatChip testId="ph13-kit-samples-received" label="Samples received" value={fmtNum(data?.samples_received)} />
        <StatChip
          testId="ph13-kit-not-returned"
          label="Sent · not returned"
          value={fmtNum(data?.samples_not_returned)}
          tone={(data?.samples_not_returned || 0) > 0 ? "bg-amber-50" : "bg-slate-50"}
        />
        <StatChip
          testId="ph13-kit-missing-tracking"
          label="Missing tracking"
          value={fmtNum(data?.missing_tracking)}
          tone={(data?.missing_tracking || 0) > 0 ? "bg-rose-50" : "bg-slate-50"}
        />
      </div>
    </Card>
  );
}


// ── 5. Commission Report ────────────────────────────────────────────────
function CommissionReport({ data }) {
  const bs = data?.by_status || {};
  const t = data?.totals || {};
  return (
    <Card
      icon={Wallet}
      title="Commission Ledger"
      testId="ph13-commission"
      headerExtra={
        <Link
          to="/admin/partners/commissions"
          data-testid="ph13-commission-link"
          className="text-[11px] font-semibold text-cyan-700 hover:underline"
        >
          Open ledger →
        </Link>
      }
    >
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {["pending", "payable", "paid", "void", "reversed"].map(k => {
          const tone = {
            pending: "bg-amber-50", payable: "bg-cyan-50",
            paid: "bg-emerald-50", void: "bg-slate-50",
            reversed: "bg-rose-50",
          }[k];
          return (
            <StatChip
              key={k}
              testId={`ph13-commission-${k}`}
              label={`${k} · count`}
              value={`${fmtNum(bs[k]?.count || 0)} · ${fmtMoney(bs[k]?.amount || 0)}`}
              tone={tone}
            />
          );
        })}
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatChip testId="ph13-commission-total-entries" label="Total entries" value={fmtNum(t.total_entries)} />
        <StatChip testId="ph13-commission-total-amount" label="Total amount" value={fmtMoney(t.total_amount)} />
        <StatChip testId="ph13-commission-total-margin" label="Margin reserved" value={fmtMoney(t.total_margin)} tone="bg-violet-50" />
        <StatChip testId="ph13-commission-cap-applied" label="Cap applied" value={fmtNum(t.total_cap_applied)} />
      </div>
    </Card>
  );
}


// ── 6. White-Label Report Summary ───────────────────────────────────────
function WhiteLabelSummary({ data }) {
  const b = data?.by_branding || {};
  const fallbacks = data?.fallbacks || [];
  const byPartner = data?.by_partner || [];
  return (
    <Card icon={FileText} title="Summary Report Branding" testId="ph13-white-label">
      <div className="grid grid-cols-3 gap-2">
        <StatChip testId="ph13-wl-white-label" label="White-label" value={fmtNum(b.white_label)} tone="bg-violet-50" />
        <StatChip testId="ph13-wl-scwt-branded" label="SCWT-branded" value={fmtNum(b.scwt_branded)} tone="bg-slate-50" />
        <StatChip testId="ph13-wl-unknown" label="Unknown" value={fmtNum(b.unknown)} />
      </div>
      {fallbacks.length > 0 && (
        <details className="mt-3" data-testid="ph13-wl-fallback-list" open>
          <summary className="text-[10px] uppercase tracking-wider font-bold text-slate-500 cursor-pointer">
            Fallback reasons ({fallbacks.length})
          </summary>
          <ul className="mt-2 space-y-1 text-xs">
            {fallbacks.map(f => (
              <li key={f.reason} className="text-slate-700">
                <span className="font-mono">{f.reason}</span> · {fmtNum(f.count)}
              </li>
            ))}
          </ul>
        </details>
      )}
      {byPartner.length > 0 && (
        <details className="mt-3" data-testid="ph13-wl-by-partner">
          <summary className="text-[10px] uppercase tracking-wider font-bold text-slate-500 cursor-pointer">
            By partner ({byPartner.length})
          </summary>
          <ul className="mt-2 space-y-1 text-xs">
            {byPartner.map(p => (
              <li key={p.partner_id} className="text-slate-700">
                <span className="font-semibold">{p.business_name || p.partner_id}</span>
                {p.partner_slug && <span className="ml-1 font-mono text-[10px] text-slate-500">{p.partner_slug}</span>}
                {" · "}{fmtNum(p.count)}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}


// ── 7. Attention / Exception Report ─────────────────────────────────────
function AttentionReport({ data }) {
  const items = data?.items || {};
  const counts = data?.counts || {};
  const ordered = [
    "review_required",
    "partner_inactive_or_missing",
    "kit_pending_over_48h",
    "report_fallback",
    "commission_blocked",
    "notification_suppressed",
    "missing_kit_tracking",
  ];
  return (
    <Card
      icon={Bell}
      title={`Attention (${counts._total || 0} item${(counts._total || 0) === 1 ? "" : "s"})`}
      testId="ph13-attention"
    >
      {ordered.every(k => (counts[k] || 0) === 0) ? (
        <div data-testid="ph13-attention-empty" className="text-xs text-slate-400 italic py-2">
          Nothing needs admin attention in this window.
        </div>
      ) : (
        <div className="space-y-3">
          {ordered.map(k => {
            const arr = items[k] || [];
            if (arr.length === 0) return null;
            return (
              <details
                key={k}
                open={arr.length <= 5}
                data-testid={`ph13-attention-block-${k}`}
                className="rounded-lg border border-slate-200 bg-slate-50"
              >
                <summary className="cursor-pointer p-2.5 flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-slate-800 flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                    {ATTENTION_LABELS[k] || k}
                  </span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900">
                    {arr.length}
                  </span>
                </summary>
                <ul className="px-3 pb-3 space-y-1 text-[11px] font-mono text-slate-700">
                  {arr.slice(0, 20).map((row, i) => (
                    <li
                      key={`${k}-${i}`}
                      data-testid={`ph13-attention-item-${k}-${i}`}
                      className="border-t border-slate-200 pt-1"
                    >
                      {row.reference && (
                        <Link
                          to={`/admin/bookings/${row.reference}`}
                          className="text-cyan-700 hover:underline"
                        >{row.reference}</Link>
                      )}
                      {row.booking_reference && !row.reference && (
                        <Link
                          to={`/admin/bookings/${row.booking_reference}`}
                          className="text-cyan-700 hover:underline"
                        >{row.booking_reference}</Link>
                      )}
                      {row.event_id && (
                        <span className="text-slate-500"> · {row.event_type} · {row.dispatch_status}</span>
                      )}
                      {row.partner_state && <span className="ml-1 text-slate-500">· {row.partner_state}</span>}
                      {row.reason && <span className="ml-1 text-slate-500">· {row.reason}</span>}
                      {row.suburb && <span className="ml-1 text-slate-500">· {row.suburb} {row.postcode}</span>}
                      {row.candidates !== undefined && (
                        <span className="ml-1 text-slate-500">· {row.candidates} candidate(s)</span>
                      )}
                    </li>
                  ))}
                </ul>
              </details>
            );
          })}
        </div>
      )}
    </Card>
  );
}


// ── Page ────────────────────────────────────────────────────────────────
function ReportAccessLog({ data }) {
  if (!data) return null;
  const rows = data.rows || [];
  const kinds = data.counts?.by_kind || {};
  const fmtWhen = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-AU", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
        timeZone: "Australia/Brisbane",
      });
    } catch { return iso; }
  };
  return (
    <Card
      icon={Eye}
      title="Partner Report Access"
      testId="ph13-report-access"
      headerExtra={
        <div className="flex gap-2">
          <StatChip label="Total opens" value={fmtNum(data.counts?.total)} testId="report-access-total" />
          <StatChip label="Summary" value={fmtNum(kinds.summary_pdf)} tone="bg-cyan-50" />
          <StatChip label="Full Analysis" value={fmtNum(kinds.coa_pdf)} tone="bg-slate-100" />
        </div>
      }
    >
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 py-4" data-testid="report-access-empty">
          No partner report opens in this window yet. Every partner open of a
          Summary or Full Analysis / COA is recorded here automatically.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="py-2 pr-3">When (AEST)</th>
                <th className="py-2 pr-3">Partner</th>
                <th className="py-2 pr-3">Booking</th>
                <th className="py-2 pr-3">Document</th>
                <th className="py-2 pr-3">Opened by</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-50" data-testid={`report-access-row-${r.id}`}>
                  <td className="py-2 pr-3 whitespace-nowrap text-slate-500">{fmtWhen(r.at)}</td>
                  <td className="py-2 pr-3 font-semibold text-slate-800">{r.partner_name || r.partner_id}</td>
                  <td className="py-2 pr-3 font-mono text-slate-600">{r.booking_reference}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${r.kind === "coa_pdf" ? "bg-slate-900 text-white" : "bg-cyan-100 text-cyan-800"}`}>
                      {r.kind === "coa_pdf" ? "Full Analysis / COA" : "Summary"}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-slate-500">
                    {r.accessed_by_email}
                    {r.accessed_by_role === "admin" && (
                      <span className="ml-1 text-[10px] font-bold text-violet-600">(admin)</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}


export default function AdminPWSReporting() {
  const [window, setWindow] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reports, setReports] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [combinedStartDate, setCombinedStartDate] = useState("");
  const [combinedEndDate, setCombinedEndDate] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    Promise.all([
      api.get("/admin/pws-reporting/booking-summary",     { params: { window } }),
      api.get("/admin/pws-reporting/source-attribution",  { params: { window } }),
      api.get("/admin/pws-reporting/revenue",             { params: { window } }),
      api.get("/admin/pws-reporting/kit-workflow",        { params: { window } }),
      api.get("/admin/pws-reporting/commission",          { params: { window } }),
      api.get("/admin/pws-reporting/white-label",         { params: { window } }),
      api.get("/admin/pws-reporting/attention",           { params: { window } }),
      api.get("/admin/pws-reporting/report-access",       { params: { window } }),
    ])
      .then(([bs, sa, rv, kw, cm, wl, at, ra]) => {
        if (!alive) return;
        setReports({
          bookingSummary:    bs.data,
          sourceAttribution: sa.data,
          revenue:           rv.data,
          kitWorkflow:       kw.data,
          commission:        cm.data,
          whiteLabel:        wl.data,
          attention:         at.data,
          reportAccess:      ra.data,
        });
      })
      .catch((e) => { if (alive) setError(formatApiError(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [window, refreshKey]);

  const totalAttention = useMemo(
    () => reports?.attention?.counts?._total || 0, [reports],
  );

  return (
    <PortalShell>
      <div
        data-testid="admin-pws-reporting-page"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      >
        <Link
          to="/admin/partners/network"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-700 hover:text-cyan-800"
          data-testid="ph13-back-to-network"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to PWS Network
        </Link>

        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">
              Aware Partner Network · Phase 13
            </div>
            <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">
              Partner Reporting
            </h1>
            <p className="text-slate-500 mt-1 text-sm max-w-3xl">
              Read-only roll-up across the entire Aware Partner Network — bookings,
              attribution, pricing, kits, commissions, white-label branding and
              admin attention items in one place. CSV export is available on the
              two summary tabs. No mutations are performed by this page.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <WindowSelector window={window} onChange={setWindow} />
            <button
              onClick={() => setRefreshKey((k) => k + 1)}
              data-testid="ph13-refresh-btn"
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-60"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
        </div>

        {totalAttention > 0 && !loading && (
          <div
            data-testid="ph13-attention-banner"
            className="mt-5 rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2 text-xs text-amber-900"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <strong>{totalAttention}</strong> item{totalAttention === 1 ? "" : "s"} need admin attention.
              See the <em>Attention</em> card below.
            </div>
          </div>
        )}

        {loading && !Object.keys(reports).length ? (
          <div
            data-testid="ph13-loading"
            className="mt-8 rounded-2xl bg-white border border-slate-200 p-10 flex items-center gap-2 text-slate-400 justify-center"
          >
            <Loader2 className="w-5 h-5 animate-spin" /> Loading reports…
          </div>
        ) : error ? (
          <div
            data-testid="ph13-error"
            className="mt-8 rounded-xl bg-rose-50 border border-rose-200 p-5 text-sm text-rose-800"
          >
            <div className="font-semibold mb-1 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Failed to load reports
            </div>
            <div className="text-xs">{error}</div>
            <button
              onClick={() => setRefreshKey((k) => k + 1)}
              data-testid="ph13-error-retry"
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-rose-100 hover:bg-rose-200 text-rose-800"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Retry
            </button>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-4">
            <PartnerCombinedSummary
              window={window}
              refreshKey={refreshKey}
              startDate={combinedStartDate}
              endDate={combinedEndDate}
              onStartDateChange={setCombinedStartDate}
              onEndDateChange={setCombinedEndDate}
            />
            <PartnerBookingSummary data={reports.bookingSummary} window={window} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SourceAttribution  data={reports.sourceAttribution} window={window} />
              <RevenueSnapshot    data={reports.revenue} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <KitWorkflowReport  data={reports.kitWorkflow} />
              <CommissionReport   data={reports.commission} />
            </div>
            <WhiteLabelSummary    data={reports.whiteLabel} />
            <ReportAccessLog      data={reports.reportAccess} />
            <AttentionReport      data={reports.attention} />
          </div>
        )}
      </div>
    </PortalShell>
  );
}
